import { ChevronDownIcon, FileTextIcon } from "lucide-react";
import { Disclosure, DisclosurePanel, Heading } from "react-aria-components";

import { InvoiceActionButtons } from "../../invoices/components/InvoiceActionButtons";
import { InvoiceActions } from "../../invoices/components/InvoiceActions";
import { InvoiceDisclosureTrigger } from "../../invoices/components/InvoiceDisclosureTrigger";
import { InvoiceStatusBadge } from "../../invoices/components/InvoiceStatusBadge";
import { useCopyInvoiceLink } from "../../invoices/hooks/useCopyInvoiceLink";
import { useInvoices } from "../../invoices/hooks/useInvoices";
import {
  formatInvoiceAmount,
  formatInvoiceLongDate,
} from "../../invoices/utils/invoice-format";
import { RECENT_INVOICES_GRID } from "../utils/recent-invoices-grid";
import { RecentInvoicesRowsSkeleton } from "./OverviewSkeleton";

const RECENT_INVOICES_LIMIT = 3;

interface RecentInvoicesSectionProps {
  onSeeAll: () => void;
  projectId: number;
}

export function RecentInvoicesSection({
  onSeeAll,
  projectId,
}: RecentInvoicesSectionProps) {
  return (
    <div className="flex flex-col gap-xl">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary">
          Recent invoices
        </h3>
        <button
          className="flex items-center gap-xs font-semibold text-sm text-button-tertiary-color-fg cursor-pointer hover:text-button-tertiary-color-fg-hover"
          onClick={onSeeAll}
          type="button"
        >
          See all invoices
        </button>
      </div>

      <RecentInvoicesContent projectId={projectId} />
    </div>
  );
}

function RecentInvoicesContent({ projectId }: { projectId: number }) {
  const { data, isError, isPending, refetch } = useInvoices({
    limit: RECENT_INVOICES_LIMIT,
    page: 1,
    projectId,
  });
  const copyInvoiceLink = useCopyInvoiceLink(projectId);

  if (isPending) {
    return <RecentInvoicesRowsSkeleton />;
  }

  if (isError) {
    return (
      <div className="py-xl">
        <p className="text-sm text-text-secondary">Failed to load invoices.</p>
        <button
          className="mt-md text-sm text-button-primary-bg hover:underline"
          onClick={() => refetch()}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (data.items.length === 0) {
    return <p className="py-xl text-sm text-text-tertiary">No invoices yet</p>;
  }

  return (
    <ul className="flex flex-col divide-y divide-border-secondary">
      {data.items.map((invoice) => (
        <li className="py-md md:py-xl" key={invoice.id}>
          <div className={RECENT_INVOICES_GRID}>
            <InvoiceFileIcon className="size-9 rounded-md bg-bg-secondary" />
            <span className="truncate text-md font-medium text-text-primary">
              {formatInvoiceLongDate(invoice.date)}
            </span>

            <span className="hidden truncate text-sm text-text-tertiary lg:block">
              {invoice.description}
            </span>

            <span className="truncate text-sm text-text-tertiary">
              {invoice.id}
            </span>

            <span className="whitespace-nowrap text-sm font-semibold text-text-primary">
              {formatInvoiceAmount(invoice.total, invoice.currency)}/mo
            </span>

            <div>
              <InvoiceStatusBadge status={invoice.status} />
            </div>

            <InvoiceActions invoice={invoice} onCopyLink={copyInvoiceLink} />
          </div>

          <Disclosure className="group md:hidden">
            <Heading className="m-0">
              <InvoiceDisclosureTrigger className="flex-col gap-xs">
                <div className="flex items-center justify-between gap-md">
                  <div className="flex min-w-0 items-center gap-md">
                    <InvoiceFileIcon className="size-8 rounded-sm bg-bg-tertiary" />
                    <span className="truncate text-md font-medium text-text-primary">
                      {formatInvoiceLongDate(invoice.date)}
                    </span>
                  </div>
                  <ChevronDownIcon
                    aria-hidden
                    className="size-5 shrink-0 text-text-secondary transition-transform group-data-[expanded]:rotate-180"
                  />
                </div>

                <span className="truncate text-sm text-text-tertiary">
                  {invoice.id}
                </span>

                <span className="text-sm text-text-secondary">
                  {invoice.description}
                </span>

                <div className="flex items-center justify-between gap-md">
                  <span className="whitespace-nowrap text-sm font-semibold text-text-primary">
                    {formatInvoiceAmount(invoice.total, invoice.currency)}/mo
                  </span>
                  <InvoiceStatusBadge status={invoice.status} />
                </div>
              </InvoiceDisclosureTrigger>
            </Heading>

            <DisclosurePanel>
              <div className="pt-lg">
                <InvoiceActionButtons
                  invoice={invoice}
                  onCopyLink={copyInvoiceLink}
                />
              </div>
            </DisclosurePanel>
          </Disclosure>
        </li>
      ))}
    </ul>
  );
}

function InvoiceFileIcon({ className }: { className: string }) {
  return (
    <div className={`flex shrink-0 items-center justify-center ${className}`}>
      <FileTextIcon className="text-fg-quaternary" size={16} />
    </div>
  );
}
